/**
 * AuthCallbackPage - Destination for Supabase invite email links.
 *
 * Flow:
 *  1. User pays via Onprofit → webhook calls admin.inviteUserByEmail.
 *  2. Supabase sends email with a link like /auth/callback?flow=paid#access_token=...
 *  3. AuthContext parses the hash tokens and applies the session.
 *  4. This page detects the session, shows "Welcome, set your password" UI.
 *  5. User sets password → backend returns a fresh session, we apply it →
 *     redirect to /onboarding?flow=paid.
 *
 * For non-paid redirects (future uses of this callback), we fall through to /.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import CoreStudioLogo from '@/components/ui/core-studio-logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Lock, KeyRound, CheckCircle2 } from 'lucide-react';

const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updatePassword, user, loading: authLoading } = useAuth();

  const flow = searchParams.get('flow') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  // Match LoginPage / ResetPasswordPage — force dark mode so the Card renders
  // dark on the gradient background (otherwise inherits whatever mode the
  // previous page left set).
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  useEffect(() => {
    if (!authLoading) setSessionReady(true);
  }, [authLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password.trim()) {
      setError('Informe uma senha.');
      return;
    }
    if (password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres.');
      return;
    }
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
      setError('Use ao menos uma letra maiúscula, uma minúscula e um caractere especial.');
      return;
    }
    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateErr } = await updatePassword(password);
      if (updateErr) {
        setError(updateErr.message);
        setLoading(false);
        return;
      }
      setSuccess(true);
      // Persist flow to sessionStorage so ProtectedRoute redirects preserve it.
      if (flow === 'paid') {
        try { sessionStorage.setItem('cai_onboarding_flow', 'paid'); } catch { /* ignore */ }
      }
      const redirectTarget = flow === 'paid' ? '/onboarding?flow=paid' : '/';
      setTimeout(() => navigate(redirectTarget, { replace: true }), 1200);
    } catch (err: any) {
      setError(err?.message || 'Erro ao definir senha.');
      setLoading(false);
    }
  };

  // Loading (waiting for AuthContext to apply hash-token session)
  if (!sessionReady) {
    return (
      <div className="min-h-screen flex items-center justify-center scanlines p-4" style={{ background: 'linear-gradient(62.4deg, #4763CA 0%, #030513 33.9%, #06091B 63.4%, #4763CA 100%)' }}>
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Verificando seu acesso…</p>
        </div>
      </div>
    );
  }

  // Invalid / expired invite link
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center scanlines p-4" style={{ background: 'linear-gradient(62.4deg, #4763CA 0%, #030513 33.9%, #06091B 63.4%, #4763CA 100%)' }}>
        <div className="w-full max-w-md space-y-8">
          <div className="flex justify-center">
            <CoreStudioLogo width={200} className="text-foreground" />
          </div>

          <Card className="border-border/50 shadow-xl">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-2xl font-bold">Link inválido ou expirado</CardTitle>
              <CardDescription>
                Esse link de acesso não é válido ou já expirou. Use "Esqueci minha senha"
                na tela de login para receber um novo.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/login')} className="w-full h-11">
                Ir para o login
              </Button>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground">
            Core Carousel © {new Date().getFullYear()}
          </p>
        </div>
      </div>
    );
  }

  // Success state (short transition before redirect)
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center scanlines p-4" style={{ background: 'linear-gradient(62.4deg, #4763CA 0%, #030513 33.9%, #06091B 63.4%, #4763CA 100%)' }}>
        <div className="w-full max-w-md space-y-8">
          <div className="flex justify-center">
            <CoreStudioLogo width={200} className="text-foreground" />
          </div>

          <Card className="border-border/50 shadow-xl">
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-3">
                <CheckCircle2 className="h-12 w-12 text-green-500" />
              </div>
              <CardTitle className="text-2xl font-bold">Tudo pronto!</CardTitle>
              <CardDescription>Estamos te levando pra dentro…</CardDescription>
            </CardHeader>
          </Card>

          <p className="text-center text-xs text-muted-foreground">
            Core Carousel © {new Date().getFullYear()}
          </p>
        </div>
      </div>
    );
  }

  // Welcome + set password form
  return (
    <div className="min-h-screen flex items-center justify-center scanlines p-4" style={{ background: 'linear-gradient(62.4deg, #4763CA 0%, #030513 33.9%, #06091B 63.4%, #4763CA 100%)' }}>
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="flex justify-center">
          <CoreStudioLogo width={200} className="text-foreground" />
        </div>

        {/* Welcome Card */}
        <Card className="border-border/50 shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-3">
              <KeyRound className="h-10 w-10 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold">Bem-vindo ao Core Carousel</CardTitle>
            <CardDescription>
              Sua compra foi aprovada. Defina uma senha para começar a usar sua conta.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New password */}
              <div>
                <Label htmlFor="password" className="text-sm font-medium">
                  Nova senha
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres, com maiúscula, minúscula e especial"
                    className="pl-10"
                    disabled={loading}
                    required
                    autoFocus
                  />
                </div>
              </div>

              {/* Confirm password */}
              <div>
                <Label htmlFor="confirmPassword" className="text-sm font-medium">
                  Confirmar senha
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repita a nova senha"
                    className="pl-10"
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              {/* Submit */}
              <Button type="submit" className="w-full h-11" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Salvando…
                  </>
                ) : (
                  <>
                    <KeyRound className="h-4 w-4 mr-2" />
                    Definir senha e entrar
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          Core Carousel © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};

export default AuthCallbackPage;
